import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./HeirloomPaywall.module.scss"

// ---------------------------------------------------------------------------
// "Heirloom" — the pitch is NOT privacy / storage / backup / search / sharing.
// It is LEGACY: a photo library is the kind of thing worth handing down —
// longer than a phone lives, longer than a single account. Premium enrolls
// your library, at full resolution, to be kept whole and passed, intact, to
// the people you name. No prior paywall pitched inheritance / continuity.
//
// The screen is NOT a hero + card. It is a DEED — a Certificate of Provenance
// on heavyweight cream paper: engraved double-rule border, a wax-sealed
// exhibit photo pinned at the head, a reverent preamble, and — the heart of
// the page, a genuine list/table skeleton — a REGISTER OF NAMED HEIRS you
// inscribe. Tap a face from "your people" and their name writes itself onto
// the next ruled line as a wax seal presses into the margin. Pricing is set
// as the deed's "Terms of Continuance" (yearly = the principal sum, dominant;
// monthly a smaller article), and the CTA executes the deed with a seal.
//
// Light, engraved-stationery art direction; blue ink + gold wax on cream —
// the brand palette used in a way no prior paywall did. Reverent / heirloom-
// proud register. Input-driven (a form you inscribe). Renders on debug data;
// portraits pre-fetched into public/heirloom.
// ---------------------------------------------------------------------------

const PUB = process.env.PUBLIC_URL ?? ""
const src = (file: string) => `${PUB}/heirloom/${file}.jpg`

type Heir = {
  id: string
  name: string
  relation: string
  photo: string
}

// "Your people" — candidates you inscribe onto the deed. Warm faces across
// generations; the point is that the library passes down a whole family line.
const PEOPLE: Heir[] = [
  { id: "ruth", name: "Ruth", relation: "Mother", photo: "heir1" },
  { id: "mara", name: "Mara", relation: "Daughter", photo: "heir2" },
  { id: "theo", name: "Theo", relation: "Son", photo: "heir3" },
  { id: "sofia", name: "Sofia", relation: "Sister", photo: "heir4" },
  { id: "daniel", name: "Daniel", relation: "Brother", photo: "heir5" },
  { id: "eli", name: "Eli", relation: "Godson", photo: "heir6" },
]

// Small engraved crest used in the letterhead and pressed into the wax seal.
function Crest({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      {/* laurel-framed monogram: an aperture blade ring, standing for a lens */}
      <g fill="none" stroke="currentColor" strokeWidth="1.4">
        <circle cx="24" cy="24" r="10.5" />
        {[0, 60, 120, 180, 240, 300].map((a) => (
          <line
            key={a}
            x1="24"
            y1="13.5"
            x2="30"
            y2="18.7"
            transform={`rotate(${a} 24 24)`}
            strokeLinecap="round"
          />
        ))}
      </g>
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      >
        {/* two laurel sprigs */}
        <path d="M13 34c-4-2-6-6-6-11" />
        <path d="M35 34c4-2 6-6 6-11" />
        {[0, 1, 2, 3].map((i) => (
          <g key={i}>
            <path d={`M${11 - i * 0.4} ${31 - i * 4.6}q-3 -1.4 -3.6 -4`} />
            <path d={`M${37 + i * 0.4} ${31 - i * 4.6}q3 -1.4 3.6 -4`} />
          </g>
        ))}
      </g>
    </svg>
  )
}

function Flourish({ className }: { className?: string }) {
  // A guilloché-ish corner flourish for the four corners of the deed.
  return (
    <svg viewBox="0 0 60 60" className={className} aria-hidden>
      <path
        d="M2 2c0 20 8 34 28 34M2 2c20 0 34 8 34 28M2 12c0 14 6 24 20 26M12 2c14 0 24 6 26 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        opacity="0.75"
      />
      <circle cx="2" cy="2" r="2.4" fill="currentColor" />
    </svg>
  )
}

function HeirloomPaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""
  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])

  // ── the register ──────────────────────────────────────────────────────────
  // `order` = ids of inscribed heirs, in the order they were named. Each fills
  // the next ruled line of the register.
  const [order, setOrder] = useState<string[]>([])
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [sealing, setSealing] = useState(false)
  const taught = useRef(false)

  const capacity = PEOPLE.length
  const inscribe = useCallback((id: string) => {
    setOrder((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }, [])

  // Teach the gesture: inscribe the first heir on load so the page is never
  // an empty form. Skipped if the user already reached in, or reduced motion.
  useEffect(() => {
    if (reduce) return
    const t = window.setTimeout(() => {
      if (!taught.current) {
        taught.current = true
        inscribe(PEOPLE[0].id)
      }
    }, 1100)
    return () => window.clearTimeout(t)
  }, [reduce, inscribe])

  const dateline = useMemo(() => {
    // Real execution date, formatted like a deed ("this 1st day of July, 2026").
    const d = new Date()
    const day = d.getDate()
    const ord =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
            ? "rd"
            : "th"
    const month = d.toLocaleDateString("en-US", { month: "long" })
    return `this ${day}${ord} day of ${month}, ${d.getFullYear()}`
  }, [])

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    setSealing(true)
    window.setTimeout(
      () => {
        if (p) native.subscribe(p)
      },
      reduce ? 0 : 620,
    )
  }

  const byId = (id: string) => PEOPLE.find((p) => p.id === id)!
  const done = order.length
  const remaining = capacity - done

  return (
    <div className={styles.root}>
      <div className={styles.deed} role="document">
        {/* engraved frame + corner flourishes */}
        <div className={styles.frameOuter} aria-hidden />
        <div className={styles.frameInner} aria-hidden />
        <Flourish className={`${styles.flourish} ${styles.flTL}`} />
        <Flourish className={`${styles.flourish} ${styles.flTR}`} />
        <Flourish className={`${styles.flourish} ${styles.flBL}`} />
        <Flourish className={`${styles.flourish} ${styles.flBR}`} />

        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className={styles.scroll}>
          {/* ── Letterhead ────────────────────────────────────────────── */}
          <header className={styles.head}>
            <img
              className={styles.exhibit}
              src={src("exhibit")}
              alt="A family photograph, pinned to the deed"
            />
            <span className={styles.exhibitSeal} aria-hidden />

            <Crest className={styles.crest} />
            <p className={styles.eyebrow}>Certificate&nbsp;Nº&nbsp;0001</p>
            <h1 className={styles.title}>Deed of Provenance</h1>
            <p className={styles.subtitle}>
              for the photographic library of the bearer
            </p>
          </header>

          <div className={styles.rule} aria-hidden>
            <span />
            <Crest className={styles.ruleMark} />
            <span />
          </div>

          {/* ── Preamble ──────────────────────────────────────────────── */}
          <p className={styles.preamble}>
            Some things are meant to outlast the phone they were made on. This
            deed enrolls your library — <em>every frame, at full resolution</em>{" "}
            — to be kept whole and, one day, handed down intact to the people
            named below.
          </p>

          {/* ── The Register (the heart) ──────────────────────────────── */}
          <section className={styles.register} aria-label="Register of named heirs">
            <div className={styles.regHead}>
              <h2>Named Heirs</h2>
              <span className={styles.regCount}>
                {done} of {capacity} inscribed
              </span>
            </div>

            <ol className={styles.lines}>
              {Array.from({ length: capacity }).map((_, i) => {
                const id = order[i]
                const heir = id ? byId(id) : undefined
                const justInk = i === done - 1
                return (
                  <li
                    key={i}
                    className={`${styles.line} ${heir ? styles.lineFilled : ""}`}
                  >
                    <span className={styles.lineNo}>{roman(i + 1)}</span>
                    {heir ? (
                      <span
                        className={`${styles.entry} ${
                          justInk && !reduce ? styles.entryInk : ""
                        }`}
                      >
                        <img
                          className={styles.entryFace}
                          src={src(heir.photo)}
                          alt=""
                        />
                        <span className={styles.entryName}>{heir.name}</span>
                        <span className={styles.leader} aria-hidden />
                        <span className={styles.entryRel}>{heir.relation}</span>
                        <span className={styles.entrySeal} aria-hidden>
                          <Crest className={styles.entrySealCrest} />
                        </span>
                      </span>
                    ) : (
                      <span className={styles.blank}>
                        <span className={styles.blankHint}>
                          {i === done ? "next to inscribe" : ""}
                        </span>
                      </span>
                    )}
                  </li>
                )
              })}
            </ol>

            {/* the tray of candidates — "your people" */}
            <div className={styles.trayWrap}>
              <p className={styles.trayLabel}>
                {remaining > 0
                  ? "Touch a name to inscribe them"
                  : "The register is complete"}
              </p>
              <div className={styles.tray}>
                {PEOPLE.map((p) => {
                  const inscribed = order.includes(p.id)
                  return (
                    <button
                      key={p.id}
                      className={`${styles.person} ${
                        inscribed ? styles.personDone : ""
                      }`}
                      onClick={() => {
                        taught.current = true
                        inscribe(p.id)
                      }}
                      disabled={inscribed}
                    >
                      <span className={styles.personRing}>
                        <img src={src(p.photo)} alt="" />
                        {inscribed && (
                          <span className={styles.personCheck} aria-hidden>
                            <svg viewBox="0 0 24 24" width="13" height="13">
                              <path
                                d="M5 12.5l4.2 4.2L19 7"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        )}
                      </span>
                      <span className={styles.personName}>{p.name}</span>
                      <span className={styles.personRel}>{p.relation}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          </section>

          <div className={styles.rule} aria-hidden>
            <span />
            <Crest className={styles.ruleMark} />
            <span />
          </div>

          {/* ── Terms of Continuance (pricing as deed articles) ───────── */}
          <section className={styles.terms} aria-label="Terms of Continuance">
            <h2 className={styles.termsHead}>Terms of Continuance</h2>

            <button
              className={`${styles.article} ${styles.articleYear} ${
                plan === "yearly" ? styles.articleOn : ""
              }`}
              onClick={() => setPlan("yearly")}
            >
              <span className={styles.articleMark} aria-hidden />
              <span className={styles.articleBody}>
                <span className={styles.articleLabel}>
                  Article I · Kept in perpetuity
                </span>
                <span className={styles.principal}>
                  <span className={styles.principalSum}>{yearlyPrice}</span>
                  <span className={styles.principalPer}>/ year</span>
                </span>
                <span className={styles.articleFine}>
                  renewed yearly — just {yearlyPerMonth}/mo
                </span>
              </span>
              {savings > 0 && (
                <span className={styles.saveWax}>save {savings}%</span>
              )}
            </button>

            <button
              className={`${styles.article} ${styles.articleMonth} ${
                plan === "monthly" ? styles.articleOn : ""
              }`}
              onClick={() => setPlan("monthly")}
            >
              <span className={styles.articleMark} aria-hidden />
              <span className={styles.articleBody}>
                <span className={styles.articleLabel}>
                  Article II · Month to month
                </span>
                <span className={styles.monthLine}>
                  <b>{monthlyPrice}</b> / month
                </span>
              </span>
            </button>
          </section>

          {/* ── Execution / seal ──────────────────────────────────────── */}
          <div className={styles.execute}>
            <button
              className={`${styles.cta} ${sealing ? styles.ctaSealing : ""}`}
              onClick={handleSubscribe}
            >
              <span className={styles.ctaSeal} aria-hidden>
                <Crest className={styles.ctaSealCrest} />
              </span>
              <span className={styles.ctaText}>
                Seal the deed —{" "}
                {plan === "yearly"
                  ? `${yearlyPrice}/yr`
                  : `${monthlyPrice}/mo`}
              </span>
            </button>

            <p className={styles.reassure}>
              Your photos are always yours. This only decides who they carry on
              to — nothing is ever locked away.
            </p>

            <div className={styles.signature}>
              <svg viewBox="0 0 180 34" className={styles.sig} aria-hidden>
                <path
                  d="M4 24c10-2 12-16 16-16s2 18 8 18 8-20 13-20 3 17 9 17 7-9 12-9 4 8 9 8 8-12 13-12 5 9 11 9 9-8 14-8 9 6 14 4 10-8 14-8 8 5 12 4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span className={styles.sigLine}>
                Sealed {dateline}, on behalf of the bearer
              </span>
            </div>

            <div className={styles.finePrint}>
              <button onClick={() => native.postNativeEvent("already_purchased")}>
                Restore
              </button>
              <span>·</span>
              <button onClick={() => native.postNativeEvent("terms")}>
                Terms
              </button>
              <span>·</span>
              <button onClick={() => native.postNativeEvent("privacy")}>
                Privacy
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function roman(n: number): string {
  return ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"][n - 1] ?? `${n}`
}

export default HeirloomPaywall
